import { getRandomInt } from "../Shared/Helpers.tsx";
import {
    CalendarMonthOptions,
    CalendarQuestionOptions,
    CalendarSpanOptions,
    CalendarSpec,
    ProblemData,
    ProblemFormatOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { calendarPrompts as en, calendarText as enText } from "../Files/Prompts/en/calendar.ts";
import { calendarPrompts as es, calendarText as esText } from "../Files/Prompts/es/calendar.ts";
import { calendarPrompts as fr, calendarText as frText } from "../Files/Prompts/fr/calendar.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const TEXT: Record<PrintedLanguage, typeof enText> = { en: enText, es: esText, fr: frText };

/**
 * Calendar sheets (ADR 0093). Each page draws one real month and asks
 * questions about it; every question carries the same
 * `problemGroup.calendar`, which `PdfGenerator/Page.tsx` draws once above
 * them (ADR 0093 §2).
 *
 * The rules ADR 0093 §3-§5 set are met by how questions are drawn, never by
 * drawing at random and checking afterwards:
 *
 * - A before-and-after question draws its start from the dates that leave
 *   room for the count inside the month, so every answer is on the drawing.
 * - No date is used twice on a page, as a question's date or an answer, so
 *   one answer never gives another away.
 * - "Find the date" uses first to fourth and last, never fifth.
 * - "How many" asks about each weekday once and the month's length once, and
 *   draws from the weekdays that occur five times half the time.
 * - A day-of-the-week question draws its date from the weekdays that have
 *   been an answer least often on the page.
 */

/**
 * The years a month is drawn from (ADR 0093 §3). Fixed forever: moving or
 * widening the window changes what every saved link prints.
 */
export const CALENDAR_FIRST_YEAR = 2025;
export const CALENDAR_LAST_YEAR = 2032;

/** How Many questions one month has: one per weekday, and its length. */
export const CALENDAR_HOW_MANY_QUESTIONS = 8;

/** One month of one year: what the drawing and the questions are about. */
export interface CalendarMonth {
    year: number;
    /** 0 is January. */
    month: number;
    /** The weekday of the 1st; 0 is Sunday. */
    firstWeekday: number;
    days: number;
}

export function calendarMonth(year: number, month: number): CalendarMonth {
    return {
        year,
        month,
        firstWeekday: new Date(Date.UTC(year, month, 1)).getUTCDay(),
        // Day 0 of the next month is the last day of this one.
        days: new Date(Date.UTC(year, month + 1, 0)).getUTCDate(),
    };
}

/** The weekday of a date in the month; 0 is Sunday. */
export function weekdayOf(month: CalendarMonth, day: number): number {
    return (month.firstWeekday + day - 1) % 7;
}

/** Every date in the month that falls on `weekday`, in order. */
export function datesOnWeekday(month: CalendarMonth, weekday: number): number[] {
    const first = ((weekday - month.firstWeekday + 7) % 7) + 1;
    const dates: number[] = [];
    for (let day = first; day <= month.days; day += 7) {
        dates.push(day);
    }
    return dates;
}

/**
 * A page's month: the named one, or any, in a year of the window. A sheet
 * never draws the same month twice while the window has another, so a
 * three-page October sheet is three different Octobers (ADR 0093 §6).
 */
function drawMonth(choice: CalendarMonthOptions | undefined, drawn: Set<string>): CalendarMonth {
    const named = Object.values(CalendarMonthOptions).indexOf(choice ?? CalendarMonthOptions.Any);
    const years = CALENDAR_LAST_YEAR - CALENDAR_FIRST_YEAR + 1;
    const possible = named > 0 ? years : years * 12;
    let month = 0;
    let year = 0;
    do {
        month = named > 0 ? named - 1 : getRandomInt(0, 11);
        year = getRandomInt(CALENDAR_FIRST_YEAR, CALENDAR_LAST_YEAR);
    } while (drawn.has(`${year}-${month}`) && drawn.size < possible);
    drawn.add(`${year}-${month}`);
    return calendarMonth(year, month);
}

/** The question types a sheet draws from, in rotation order. */
export function calendarQuestionTypes(question: CalendarQuestionOptions): CalendarQuestionOptions[] {
    const reading = [
        CalendarQuestionOptions.DayOfWeek,
        CalendarQuestionOptions.FindDate,
        CalendarQuestionOptions.HowMany,
    ];
    if (question === CalendarQuestionOptions.Reading) {
        return reading;
    }
    if (question === CalendarQuestionOptions.Mixed) {
        return [...reading, CalendarQuestionOptions.BeforeAfter];
    }
    return [question];
}

/** Whether a sheet asks any before-and-after question, so the span means something. */
export function asksBeforeAfter(question: CalendarQuestionOptions | undefined): boolean {
    return calendarQuestionTypes(question ?? CalendarQuestionOptions.Mixed).includes(CalendarQuestionOptions.BeforeAfter);
}

/** How far one before-and-after question counts (ADR 0093 §5). */
function drawCount(span: CalendarSpanOptions): { count: number; weeks: boolean } {
    switch (span) {
        case CalendarSpanOptions.OneDay:
            return { count: 1, weeks: false };
        case CalendarSpanOptions.UpToTwentyDays:
            return { count: getRandomInt(2, 20), weeks: false };
        default:
            return getRandomInt(0, 1) === 0
                ? { count: getRandomInt(2, 6), weeks: false }
                : { count: getRandomInt(1, 3), weeks: true };
    }
}

/** What a page has already asked about its month, so the next question is new. */
interface PageUsage {
    dates: Set<number>;
    /** How many day-of-the-week answers each weekday has been. */
    weekdayAnswers: number[];
    weekdaysCounted: Set<number>;
    lengthAsked: boolean;
}

function pick<T>(items: T[]): T {
    return items[getRandomInt(0, items.length - 1)];
}

function tryBuildQuestion(
    type: CalendarQuestionOptions,
    month: CalendarMonth,
    span: CalendarSpanOptions,
    used: PageUsage,
    language: PrintedLanguage
): { prompt: string; answer: string; commit: () => void } | null {
    const t = TEXT[language];
    const p = PROMPTS[language];
    const monthName = t.months[month.month];
    const dateText = (day: number) => t.date(monthName, day);
    const free = (day: number) => day >= 1 && day <= month.days && !used.dates.has(day);

    switch (type) {
        case CalendarQuestionOptions.DayOfWeek: {
            const open = Array.from({ length: month.days }, (_, i) => i + 1).filter(free);
            if (open.length === 0) return null;
            const fewest = Math.min(...open.map((day) => used.weekdayAnswers[weekdayOf(month, day)]));
            const day = pick(open.filter((d) => used.weekdayAnswers[weekdayOf(month, d)] === fewest));
            const weekday = weekdayOf(month, day);
            return {
                prompt: p.dayOfWeek(dateText(day)),
                answer: t.weekdays[weekday],
                commit: () => {
                    used.dates.add(day);
                    used.weekdayAnswers[weekday]++;
                },
            };
        }
        case CalendarQuestionOptions.FindDate: {
            // Every (ordinal, weekday) whose date is still free; "last" is the
            // fifth choice beside first to fourth, so it comes up a fifth of
            // the time.
            const options: { ordinal: string; weekday: number; day: number }[] = [];
            for (let weekday = 0; weekday < 7; weekday++) {
                const dates = datesOnWeekday(month, weekday);
                t.ordinals.forEach((ordinal, i) => options.push({ ordinal, weekday, day: dates[i] }));
                options.push({ ordinal: t.last, weekday, day: dates[dates.length - 1] });
            }
            const open = options.filter(({ day }) => free(day));
            if (open.length === 0) return null;
            const { ordinal, weekday, day } = pick(open);
            return {
                prompt: p.findDate(ordinal, t.weekdays[weekday]),
                answer: dateText(day),
                commit: () => used.dates.add(day),
            };
        }
        case CalendarQuestionOptions.HowMany: {
            const weekdays = [0, 1, 2, 3, 4, 5, 6].filter((w) => !used.weekdaysCounted.has(w));
            // The month's length is one choice among the open weekdays, so a
            // page asks it about as often as it asks about any one weekday.
            const choices = used.lengthAsked ? weekdays.length : weekdays.length + 1;
            if (choices === 0) return null;
            if (!used.lengthAsked && getRandomInt(0, choices - 1) === 0) {
                return {
                    prompt: p.howManyDays(monthName),
                    answer: String(month.days),
                    commit: () => { used.lengthAsked = true; },
                };
            }
            if (weekdays.length === 0) return null;
            // Half the time, a weekday that occurs five times, when the month
            // has one left: "always write 4" should score about half.
            const five = weekdays.filter((w) => datesOnWeekday(month, w).length === 5);
            const four = weekdays.filter((w) => datesOnWeekday(month, w).length === 4);
            const pool = five.length > 0 && (four.length === 0 || getRandomInt(0, 1) === 0) ? five : four;
            const weekday = pick(pool);
            return {
                prompt: p.howManyWeekday(t.weekdaysPlural[weekday], monthName),
                answer: String(datesOnWeekday(month, weekday).length),
                commit: () => used.weekdaysCounted.add(weekday),
            };
        }
        default: {
            // BeforeAfter. The start is drawn from the dates that leave room
            // for the count inside the month (ADR 0093 §3), both ends unused.
            const { count, weeks } = drawCount(span);
            const days = weeks ? count * 7 : count;
            const after = getRandomInt(0, 1) === 0;
            // One day pairs two neighbouring dates. A pair placed an odd
            // number of dates into a run of free ones strands a date on each
            // side, and a pinned February page of ten could run out, so a pair
            // starts an even number of dates into its run: every run then
            // still holds as many pairs as it can.
            const evenIntoRun = (low: number) => {
                let runStart = low;
                while (free(runStart - 1)) runStart--;
                return (low - runStart) % 2 === 0;
            };
            const starts = Array.from({ length: month.days }, (_, i) => i + 1)
                .filter((start) => free(start) && free(after ? start + days : start - days))
                .filter((start) => days !== 1 || evenIntoRun(after ? start : start - 1));
            if (starts.length === 0) return null;
            const start = pick(starts);
            const end = after ? start + days : start - days;
            return {
                prompt: p.beforeAfter(count, weeks, after, dateText(start)),
                answer: dateText(end),
                commit: () => {
                    used.dates.add(start);
                    used.dates.add(end);
                },
            };
        }
    }
}

/** The drawing for one month, in the printed language. */
export function calendarSpecFor(month: CalendarMonth, language: PrintedLanguage): CalendarSpec {
    const t = TEXT[language];
    return {
        title: t.title(t.months[month.month], month.year),
        weekdays: [...t.weekdaysShort],
        firstWeekday: month.firstWeekday,
        days: month.days,
    };
}

/**
 * One page: a month and `want` distinct questions about it, all tagged with
 * the page's group so the month is drawn once. A type with nothing new left
 * to ask is skipped; a page comes back short only if every type has run out,
 * which the offered counts never reach.
 */
export function buildCalendarPage(
    settings: Pick<ISettingsProps, 'CalendarQuestion' | 'CalendarSpan' | 'CalendarMonth'>,
    want: number,
    groupKey: string,
    startKey: number,
    language: PrintedLanguage,
    drawn: Set<string> = new Set()
): { problems: ProblemData[]; nextKey: number; month: CalendarMonth } {
    const month = drawMonth(settings.CalendarMonth, drawn);
    const spec = calendarSpecFor(month, language);
    const types = calendarQuestionTypes(settings.CalendarQuestion ?? CalendarQuestionOptions.Mixed);
    const span = settings.CalendarSpan ?? CalendarSpanOptions.DaysAndWeeks;
    const used: PageUsage = {
        dates: new Set(),
        weekdayAnswers: [0, 0, 0, 0, 0, 0, 0],
        weekdaysCounted: new Set(),
        lengthAsked: false,
    };
    const problems: ProblemData[] = [];
    let key = startKey;
    // Start the rotation anywhere, so a short Reading page does not always
    // open with a day-of-the-week question (ADR 0042).
    let typeIndex = getRandomInt(0, types.length - 1);
    let stalled = 0;

    while (problems.length < want && stalled < 12) {
        const type = types[typeIndex % types.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, month, span, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.commit();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            calendarQuestion: type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.Calendar, calendar: spec },
        });
    }

    return { problems, nextKey: key, month };
}

/** A page-shaped set of calendar problems, one month a page. */
export function generateCalendarPages(settings: ISettingsProps): ProblemData[][] | null {
    const language = printedLanguageOf(settings);
    const pages: ProblemData[][] = [];
    const drawn = new Set<string>();
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const built = buildCalendarPage(
            settings,
            settings.NumberOfProblemsPerPage,
            `calendar:${page}`,
            nextKey,
            language,
            drawn
        );
        nextKey = built.nextKey;
        pages.push(built.problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
